import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  InboxOutlined,
} from '@ant-design/icons'
import { Image, StyleSheet, Text, View } from '@react-pdf/renderer'
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Image as AntImage,
  Input,
  message,
  Space,
  Typography,
  Upload,
  UploadFile,
} from 'antd'
import { UploadChangeParam } from 'antd/es/upload'
import CantileverButton from 'components/CantileverButton'
import { createGeckoReportData } from 'features/assetDatabase/utils/createGeckoReportData'
import { useRolodexEdit } from 'features/fde/rolodexAPI'
import { PageFooter } from 'features/reporting/pdfGeneration/components/shared/PageFooter'
import { PageHeader } from 'features/reporting/pdfGeneration/components/shared/PageHeader'
import { ReportPage } from 'features/reporting/pdfGeneration/components/shared/ReportPage'
import { PdfProps, ReportSection } from 'features/reporting/types'
import { useRolodexEntries, useRolodexSignedURLs } from 'features/rolodex'
import { AddendumPages } from 'pages/reporting/reports/rug/components/AddendumPages'
import { ImagePageHeader } from 'pages/reporting/reports/rug/components/ImagePageHeader'
import { REPORT_COLORS } from 'pages/reporting/reports/rug/constants/rugReportStyles'
import { RugReportSectionId } from 'pages/reporting/reports/rug/rugReportData'
import { useRugReportOps } from 'pages/reporting/reports/rug/RugReportProvider'
import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import styled from 'styled-components'
import {
  GeckoFileCategory,
  RolodexEntryDataType,
  RolodexEntryPatchJson,
  RolodexEntryPutJson,
  RolodexEntryType,
} from 'types'
import { v4 as uuidv4 } from 'uuid'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1rem;
`

const ImageSelectionCard = styled(Card)`
  margin-bottom: 1rem;

  .ant-card-body {
    display: flex;
    gap: 1rem;
    align-items: flex-start;
  }
`

const OrderBadge = styled.div`
  position: absolute;
  top: 8px;
  left: 8px;
  background: #1890ff;
  color: white;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 14px;
  z-index: 1;
`

const ImagePreview = styled.div`
  position: relative;
  width: 150px;
  height: 150px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  overflow: hidden;
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`

const ImageDetails = styled.div`
  flex: 1;
`

const ReorderButtons = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

const UploadCard = styled(Card)`
  margin-bottom: 2rem;

  .ant-card-body {
    padding: 1.5rem;
  }
`

const UploadArea = styled.div`
  .ant-upload-list {
    margin-top: 1rem;
  }
`

const CaptionContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
`

const CaptionLabel = styled(Typography.Text)`
  min-width: 60px;
  white-space: nowrap;
`

const MetadataText = styled(Typography.Text)`
  display: block;
  margin-top: 0.5rem;
`

const PositionText = styled(Typography.Text)`
  display: block;
  margin-top: 0.5rem;
  font-weight: bold;
`

const UploadIcon = styled(InboxOutlined)`
  font-size: 24px;
  color: #1890ff;
`

const UploadText = styled.div`
  margin-top: 8px;
`

const NoPreviewContainer = styled.div`
  padding: 1rem;
  text-align: center;
`

const SaveButtonContainer = styled.div`
  margin-top: 1rem;
`

const IMAGE_MAX_HEIGHT = 320
const IMAGE_PAIR_CONTAINER_HEIGHT = 420

const pageStyles = StyleSheet.create({
  imagePage: {
    paddingTop: 8,
    paddingBottom: 40,
    paddingHorizontal: 20,
  },
  imageSectionLabelContainer: {
    marginBottom: 8,
  },
  imageSectionLabel: {
    fontSize: 12,
    fontWeight: 'bold',
    color: REPORT_COLORS.HEADER_TEXT,
  },
  imageRow: {
    display: 'flex',
    flexDirection: 'row',
    height: IMAGE_PAIR_CONTAINER_HEIGHT,
    gap: 40,
    paddingBottom: 16,
  },
  imagePanel: {
    flex: 1,
    height: IMAGE_PAIR_CONTAINER_HEIGHT,
    alignItems: 'center',
  },
  imageCaption: {
    fontSize: 10,
    fontWeight: 'bold',
    color: REPORT_COLORS.HEADER_TEXT,
    marginBottom: 8,
    textAlign: 'center',
    minHeight: 14,
  },
  imageContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
  },
  image: {
    width: '100%',
    maxWidth: '100%',
    maxHeight: IMAGE_MAX_HEIGHT,
    objectFit: 'contain',
  },
})

const RenderReact: FC = () => {
  const { unit, formData, handleImagesSectionFinish, inspections } =
    useRugReportOps()
  const { rolodexPut, rolodexPatch, rolodexUploadImage } = useRolodexEdit()

  const [selectedReportIds, setSelectedReportIds] = useState<string[]>(() => {
    return formData.imagesSectionData?.imageReportIds || []
  })

  const [uploadFiles, setUploadFiles] = useState<UploadFile[]>([])
  const [isUploading, setIsUploading] = useState(false)
  const [editedCaptions, setEditedCaptions] = useState<Record<string, string>>(
    {}
  )

  const hasLocalChangesRef = useRef(false)

  useEffect(() => {
    const formDataIds = formData.imagesSectionData?.imageReportIds || []

    if (!hasLocalChangesRef.current) {
      setSelectedReportIds(formDataIds)
    }
  }, [formData.imagesSectionData?.imageReportIds])

  const {
    reports,
    isLoading,
    forceRefresh: refetchReports,
  } = useRolodexEntries(
    unit
      ? { type: RolodexEntryType.REPORT, tags: { unit: unit.id } }
      : undefined
  )

  const imageReports = useMemo(() => {
    return reports.filter(
      (report) => report.data.file_category === GeckoFileCategory.IMAGE
    )
  }, [reports])

  const imageReportFileRefs = useMemo(() => {
    return imageReports.map((report) => ({
      entry_id: report.id,
      path: report.id,
    }))
  }, [imageReports])

  const { results: imageReportFileRefsResults } =
    useRolodexSignedURLs(imageReportFileRefs)

  const handleMoveUp = useCallback(
    (reportId: string) => {
      hasLocalChangesRef.current = true
      setSelectedReportIds((prev) => {
        const index = prev.indexOf(reportId)
        if (index <= 0) return prev

        const newOrder = [...prev]
        ;[newOrder[index - 1], newOrder[index]] = [
          newOrder[index],
          newOrder[index - 1],
        ]
        return newOrder
      })
    },
    [setSelectedReportIds]
  )

  const handleMoveDown = useCallback(
    (reportId: string) => {
      hasLocalChangesRef.current = true
      setSelectedReportIds((prev) => {
        const index = prev.indexOf(reportId)
        if (index === -1 || index >= prev.length - 1) return prev

        const newOrder = [...prev]
        ;[newOrder[index], newOrder[index + 1]] = [
          newOrder[index + 1],
          newOrder[index],
        ]
        return newOrder
      })
    },
    [setSelectedReportIds]
  )

  const handleSelectionChange = useCallback(
    (reportId: string, checked: boolean) => {
      hasLocalChangesRef.current = true
      setSelectedReportIds((prev) => {
        if (checked) {
          return [...prev, reportId]
        } else {
          return prev.filter((id) => id !== reportId)
        }
      })
    },
    [setSelectedReportIds]
  )

  const handleCaptionChange = useCallback(
    (reportId: string, newCaption: string) => {
      setEditedCaptions((prev) => ({
        ...prev,
        [reportId]: newCaption,
      }))
    },
    []
  )

  const handleUploadChange = useCallback(
    (info: UploadChangeParam<UploadFile>) => {
      setUploadFiles([...info.fileList])
    },
    []
  )

  const handleUpload = useCallback(async () => {
    if (!unit) {
      message.error('No unit selected')
      return
    }

    if (uploadFiles.length === 0) {
      message.error('Please select at least one image to upload')
      return
    }

    setIsUploading(true)

    try {
      const imageReportEntries: RolodexEntryPutJson[] = uploadFiles.map(
        (file) => {
          const fileObj = file.originFileObj as File

          const patchData = createGeckoReportData({
            file: fileObj,
            fileCategory: GeckoFileCategory.IMAGE,
            units: [unit.id],
            components: [],
            jobs: [],
            inspections: inspections?.map((inspection) => inspection.id) || [],
          })

          return {
            id: uuidv4(),
            type: RolodexEntryType.REPORT,
            data: patchData as unknown as RolodexEntryDataType,
            tags: {
              [RolodexEntryType.UNIT]: unit.id,
              [RolodexEntryType.SITE]: unit.tags.site,
              [RolodexEntryType.ORGANIZATION]: unit.tags.organization,
            },
          }
        }
      )

      await rolodexPut(imageReportEntries)

      await Promise.all(
        imageReportEntries.map((entry, index) => {
          const file = uploadFiles[index].originFileObj as File
          return rolodexUploadImage(entry.id, file, entry.id)
        })
      )

      message.success('Images uploaded successfully')
      setUploadFiles([])

      await refetchReports()
    } catch (error) {
      message.error('Failed to upload images')
      console.error('Upload error:', error)
    } finally {
      setIsUploading(false)
    }
  }, [
    unit,
    uploadFiles,
    rolodexPut,
    rolodexUploadImage,
    refetchReports,
    inspections,
  ])

  const handleSave = useCallback(async () => {
    try {
      const captionPatches: RolodexEntryPatchJson[] = Object.entries(
        editedCaptions
      ).map(([reportId, caption]) => ({
        id: reportId,
        data: {
          file_caption: caption,
        },
      }))

      if (captionPatches.length > 0) {
        await rolodexPatch(captionPatches)
      }

      handleImagesSectionFinish({
        imageReportIds: selectedReportIds,
      })

      setEditedCaptions({})

      await refetchReports()

      message.success('Changes saved successfully')
    } catch (error) {
      message.error('Failed to save changes')
      console.error('Save error:', error)
    }
  }, [
    selectedReportIds,
    editedCaptions,
    handleImagesSectionFinish,
    rolodexPatch,
    refetchReports,
  ])

  const hasChanges = useMemo(() => {
    const currentIds = formData.imagesSectionData?.imageReportIds || []
    const hasIdChanges =
      JSON.stringify([...currentIds].sort()) !==
      JSON.stringify([...selectedReportIds].sort())
    const hasCaptionChanges = Object.keys(editedCaptions).length > 0
    return hasIdChanges || hasCaptionChanges
  }, [
    formData.imagesSectionData?.imageReportIds,
    selectedReportIds,
    editedCaptions,
  ])

  const sortedReports = useMemo(() => {
    const selectedReportsInOrder = selectedReportIds
      .map((id) => imageReports.find((report) => report.id === id))
      .filter((report) => report !== undefined)

    const unselectedReports = imageReports.filter(
      (report) => !selectedReportIds.includes(report.id)
    )

    return [...selectedReportsInOrder, ...unselectedReports]
  }, [imageReports, selectedReportIds])

  if (isLoading) {
    return <div>Loading available images...</div>
  }

  return (
    <Container>
      <UploadCard
        title='Upload New Images'
        extra={
          uploadFiles.length > 0 && (
            <Button
              type='primary'
              onClick={handleUpload}
              loading={isUploading}
              icon={<InboxOutlined />}>
              Upload {uploadFiles.length} Image
              {uploadFiles.length > 1 ? 's' : ''}
            </Button>
          )
        }>
        <UploadArea>
          <Upload
            multiple
            accept='image/*'
            beforeUpload={() => false}
            onChange={handleUploadChange}
            fileList={uploadFiles}
            onRemove={(file) => {
              setUploadFiles(uploadFiles.filter((f) => f.uid !== file.uid))
            }}
            listType='picture-card'>
            <div>
              <UploadIcon />
              <UploadText>Click to Select Images</UploadText>
            </div>
          </Upload>
        </UploadArea>
      </UploadCard>

      <Typography.Title level={4}>Select Images for Report</Typography.Title>
      <Typography.Text type='secondary'>
        Select which images to include in the report. Images will be displayed 2
        per page in the PDF.
      </Typography.Text>

      {imageReports.length === 0 ? (
        <Alert
          message='No Images Available'
          description='No report images have been uploaded for this asset.'
          type='info'
          showIcon
          style={{ marginTop: '1rem' }}
        />
      ) : (
        <Space
          orientation='vertical'
          style={{ width: '100%', marginTop: '1rem' }}>
          {sortedReports.map((entry) => {
            const isSelected = selectedReportIds.includes(entry.id)
            const selectedIndex = selectedReportIds.indexOf(entry.id)
            const canMoveUp = isSelected && selectedIndex > 0
            const canMoveDown =
              isSelected && selectedIndex < selectedReportIds.length - 1

            const signedUrl = imageReportFileRefsResults.find(
              (result) => result.entry_id === entry.id
            )?.signed_url

            return (
              <ImageSelectionCard key={entry.id}>
                <Checkbox
                  checked={isSelected}
                  onChange={(e) =>
                    handleSelectionChange(entry.id, e.target.checked)
                  }
                />
                <ImagePreview>
                  {isSelected && <OrderBadge>{selectedIndex + 1}</OrderBadge>}
                  {signedUrl ? (
                    <AntImage
                      src={signedUrl}
                      alt={entry.data.file_caption}
                      preview={{
                        src: signedUrl,
                      }}
                    />
                  ) : (
                    <NoPreviewContainer>
                      No preview available
                    </NoPreviewContainer>
                  )}
                </ImagePreview>
                <ImageDetails>
                  <CaptionContainer>
                    <CaptionLabel strong>Caption:</CaptionLabel>
                    <Input
                      value={
                        editedCaptions[entry.id] ??
                        entry.data.file_caption ??
                        ''
                      }
                      onChange={(e) =>
                        handleCaptionChange(entry.id, e.target.value)
                      }
                      placeholder='Enter image caption'
                    />
                  </CaptionContainer>
                  {entry.data.file_source && (
                    <Typography.Text type='secondary'>
                      Source: {entry.data.file_source}
                    </Typography.Text>
                  )}
                  <MetadataText type='secondary'>
                    Uploaded:{' '}
                    {entry.created_at
                      ? new Date(entry.created_at).toLocaleDateString()
                      : 'N/A'}
                  </MetadataText>
                  {isSelected && (
                    <PositionText type='secondary'>
                      Position: {selectedIndex + 1} of{' '}
                      {selectedReportIds.length}
                    </PositionText>
                  )}
                </ImageDetails>
                <ReorderButtons>
                  <Button
                    icon={<ArrowUpOutlined />}
                    disabled={!canMoveUp}
                    onClick={() => handleMoveUp(entry.id)}
                    size='small'
                    title='Move up'
                  />
                  <Button
                    icon={<ArrowDownOutlined />}
                    disabled={!canMoveDown}
                    onClick={() => handleMoveDown(entry.id)}
                    size='small'
                    title='Move down'
                  />
                </ReorderButtons>
              </ImageSelectionCard>
            )
          })}
        </Space>
      )}

      <SaveButtonContainer>
        <CantileverButton primary onClick={handleSave} disabled={!hasChanges}>
          Save Changes
        </CantileverButton>
      </SaveButtonContainer>
    </Container>
  )
}

const RenderPDF: React.FC<PdfProps> = ({ onRender }) => {
  const { formData, assetPictures } = useRugReportOps()

  const renderImagePanel = (picture?: (typeof assetPictures)[number]) => {
    return (
      <View style={pageStyles.imagePanel}>
        <Text style={pageStyles.imageCaption}>
          {picture?.imageCaption ?? ''}
        </Text>
        <View style={pageStyles.imageContainer}>
          {picture ? (
            <Image
              src={picture.pdfSrc}
              cache={false}
              style={pageStyles.image}
            />
          ) : null}
        </View>
      </View>
    )
  }

  return (
    <>
      {assetPictures.map((image, index) => {
        if (index % 2 !== 0) return null

        const secondImage =
          index + 1 < assetPictures.length
            ? assetPictures[index + 1]
            : undefined

        const tracker =
          index === 0
            ? {
                sectionId: RugReportSectionId.IMAGES,
                onRender,
              }
            : undefined

        return (
          <ReportPage
            key={index}
            id={index === 0 ? 'images' : undefined}
            size='LETTER'
            orientation='landscape'
            pageStyle={pageStyles.imagePage}
            wrap={false}
            tracker={tracker}
            contentStyle={{ margin: 0 }}
            header={<PageHeader title='IMAGES' />}
            footer={
              <PageFooter
                jobNumber={formData.mainInfo.jobNumber}
                revisionNumber={formData.revisionMetadata.revisionNumber}
              />
            }>
            <ImagePageHeader />

            <View style={pageStyles.imageSectionLabelContainer}>
              <Text style={pageStyles.imageSectionLabel}>Digital Images</Text>
            </View>

            <View style={pageStyles.imageRow}>
              {renderImagePanel(image)}
              {renderImagePanel(secondImage)}
            </View>
          </ReportPage>
        )
      })}
      <AddendumPages
        sectionId={RugReportSectionId.IMAGES}
        sectionTitle='Images'
        onRender={assetPictures.length === 0 ? onRender : undefined}
      />
    </>
  )
}

export const ImagesSection: ReportSection = {
  id: RugReportSectionId.IMAGES,
  title: 'Images',
  description: 'Asset images and documentation',
  required: false,
  RenderReact,
  RenderPDF,
  requiredFields: [],
}
